import { Link } from "react-router-dom";
import { Mail, Instagram } from "lucide-react";
import { FOOTER } from "@/constants/testIds";
import { toast } from "sonner";

function TikTokIcon({ className }) {
  return (
    <svg
      viewBox="0 0 32 32"
      className={className}
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M22.5 3h-3.6v18.9a3.6 3.6 0 1 1-3.6-3.6c.2 0 .4 0 .6.05v-3.65a7.25 7.25 0 1 0 6.25 7.2v-9.55a10.35 10.35 0 0 0 5.85 1.85V10.55a6.55 6.55 0 0 1-5.5-7.55z"/>
    </svg>
  );
}

async function handleShareSMS() {
  const url = window.location.origin;
  const body = encodeURIComponent(
    `Check out Waylio — delivery for small businesses in Metro Atlanta. ${url}`
  );
  // On mobile, sms: opens the native SMS composer with a prefilled message
  const isMobile = /Android|iPhone|iPad/i.test(navigator.userAgent);
  if (isMobile) {
    window.location.href = `sms:?&body=${body}`;
    return;
  }
  // Desktop fallback — copy the share text to clipboard
  try {
    await navigator.clipboard.writeText(
      `Check out Waylio — delivery for small businesses in Metro Atlanta. ${url}`
    );
    toast.success("Share message copied — paste into any SMS.");
  } catch {
    toast.error("Could not copy — long-press to copy the link.");
  }
}

export default function Footer() {
  return (
    <footer
      data-testid={FOOTER.root}
      className="bg-forest text-cream pt-11 md:pt-14 pb-7 relative overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 relative">
        <div className="max-w-4xl mx-auto grid grid-cols-12 gap-4 md:gap-8">
          {/* Spread The Word — left, content centered */}
          <div className="col-span-12 md:col-span-4 flex flex-col items-center text-center">
            <div className="text-[10px] uppercase tracking-[0.3em] text-cream/60 mb-4">
              Spread The Word
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={handleShareSMS}
                data-testid="footer-share-sms"
                aria-label="Share Waylio via SMS"
                className="w-11 h-11 inline-flex items-center justify-center border border-cream/25 text-cream text-[11px] font-bold tracking-[0.08em] hover:border-terracotta hover:text-terracotta transition-colors"
              >
                SMS
              </button>
              <a
                href="https://instagram.com/gowaylio"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-instagram"
                aria-label="Waylio on Instagram (@gowaylio)"
                className="w-11 h-11 inline-flex items-center justify-center border border-cream/25 hover:border-terracotta hover:text-terracotta transition-colors"
              >
                <Instagram className="w-5 h-5" strokeWidth={1.6} />
              </a>
              <a
                href="https://tiktok.com/@gowaylio"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-tiktok"
                aria-label="Waylio on TikTok (@gowaylio)"
                className="w-11 h-11 inline-flex items-center justify-center border border-cream/25 hover:border-terracotta hover:text-terracotta transition-colors"
              >
                <TikTokIcon className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Reach us — middle, content centered */}
          <div className="col-span-12 md:col-span-4 flex flex-col items-center text-center">
            <div className="text-[10px] uppercase tracking-[0.3em] text-cream/60 mb-4">
              Reach us
            </div>
            <a
              href="mailto:support@waylio.com"
              className="inline-flex items-center gap-2 text-cream text-base link-underline"
            >
              <Mail className="w-4 h-4" />
              support@waylio.com
            </a>
          </div>

          {/* Navigate — right, content centered */}
          <div className="col-span-12 md:col-span-4 flex flex-col items-center text-center">
            <div className="text-[10px] uppercase tracking-[0.3em] text-cream/60 mb-4">
              Navigate
            </div>
            <ul className="space-y-1.5 text-base">
              <li><Link to="/services" className="link-underline">Services</Link></li>
              <li><Link to="/about" className="link-underline">About</Link></li>
              <li><Link to="/how-we-work" className="link-underline">How We Work</Link></li>
              <li><Link to="/client-stories" className="link-underline">Client Stories</Link></li>
              <li>
                <Link data-testid={FOOTER.linkResources} to="/resources" className="link-underline">
                  Resources
                </Link>
              </li>
              <li>
                <Link data-testid={FOOTER.linkContact} to="/contact" className="link-underline">
                  Contact
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-8 pt-4 border-t border-cream/15 text-center text-xs text-cream/60">
          <div>© {new Date().getFullYear()} Waylio Technologies LLC. All rights reserved.</div>
        </div>
      </div>
    </footer>
  );
}
